/**
 * How a review's date reads on a card: "This week", "Last month", "2 years ago".
 *
 * The ladder the design draws, most recent rung first. A review is old news rather than
 * an event, so the design asks for roughly how long ago rather than a date a customer
 * has to work out for themselves.
 */
const THIS_WEEK = 'This week';
const THIS_MONTH = 'This month';
const LAST_MONTH = 'Last month';
const THIS_YEAR = 'This year';
const LAST_YEAR = 'Last year';
/* Only ever plural: a single year back is "Last year", so the count starts at two. */
const yearsAgo = (years: number) => `${years} years ago`;

/**
 * An ISO date with no time in it is parsed as UTC by `new Date`, which lands on the
 * previous day at any negative offset and would label a review a week early on its
 * boundary. Parsed as local midnight instead, so the rung is about the day the customer
 * is looking at. A value that does carry a time is left to `Date`, which reads the
 * offset in it.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const parseDate = (value: string) => {
  if (!DATE_ONLY.test(value)) {
    return new Date(value);
  }

  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);

  /* The multi-argument constructor normalises out-of-range parts rather than rejecting
     them, so "2025-02-30" would come back as 2 March and read as a real date. Checking
     the parts round-trip is what makes a nonexistent date land on the `Invalid Date`
     path below instead of on a confident wrong label. */
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return new Date(NaN);
  }

  return parsed;
};

/**
 * Monday, per UK convention, so "This week" means the week a customer is in rather than
 * the last seven days.
 */
const startOfWeek = (date: Date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  /* `getDay` counts from Sunday, which is six days into a week that starts on Monday. */
  const daysSinceMonday = (start.getDay() + 6) % 7;

  start.setDate(start.getDate() - daysSinceMonday);

  return start;
};

/** Months since year zero, so a difference of one is last month across a new year too. */
const monthsSinceEpoch = (date: Date) => date.getFullYear() * 12 + date.getMonth();

/**
 * Roughly how long ago a review was left, worded as the design's ladder.
 *
 * Every rung is a calendar boundary rather than a count of days, because "This month"
 * and "This year" can only mean the calendar's. One consequence worth knowing: a review
 * left on Sunday reads "This week" that day and "Last month" on Monday morning if the
 * month turned with the week. The alternative, a rolling seven days, would instead have
 * "This week" overlap "Last month", which reads worse.
 *
 * The rungs are checked most recent first, so a review from December read in January is
 * "Last month" rather than "Last year", both of which are true.
 *
 * `now` is injectable and not only for tests: a page that renders this on the server and
 * again on the client can pass one timestamp to both, rather than having the label
 * change under a hydration on the rare tick where a boundary falls between them.
 *
 * Returns `undefined` for a date it cannot read, so a caller leaves the date off the
 * card rather than showing a guess or the words "Invalid Date". The card already treats
 * a missing label that way.
 */
export const getRelativeDateLabel = (date: string, now: Date = new Date()): string | undefined => {
  const then = parseDate(date);

  if (Number.isNaN(then.getTime())) {
    return undefined;
  }

  /* Also where a date in the future lands, from a clock skewed against ours or a feed
     that sends one: "This week" is the closest true thing, and the alternative is
     arithmetic that reads "-1 years ago". */
  if (then >= startOfWeek(now)) {
    return THIS_WEEK;
  }

  const months = monthsSinceEpoch(now) - monthsSinceEpoch(then);

  if (months === 0) {
    return THIS_MONTH;
  }

  if (months === 1) {
    return LAST_MONTH;
  }

  const years = now.getFullYear() - then.getFullYear();

  if (years === 0) {
    return THIS_YEAR;
  }

  if (years === 1) {
    return LAST_YEAR;
  }

  return yearsAgo(years);
};
